import { SLOWMO_SPEED } from "./sim/tuning";

// Cheats (#119, THPS style): toggles in Pause > Settings > Cheats that change
// the sim. Sim-free and render-free like `settings.ts`: the skater reads the
// ones that change how it rides (`Skater.cheats`), Game the slow-mo and
// Mirror mode (which loads the map flipped, `mirror.ts`). Any cheat
// on shows a "CHEATS" tag on the HUD, and goals done meanwhile don't count
// (no challenge checks, no stat points).

export const CHEATS = ["moon", "balance", "slowmo", "mirror", "special"] as const;
export type CheatId = (typeof CHEATS)[number];
export type Cheats = Readonly<Record<CheatId, boolean>>;

export const NO_CHEATS: Cheats = { moon: false, balance: false, slowmo: false, mirror: false, special: false };

export const CHEAT_LABELS: Record<CheatId, string> = {
  moon: "Moon gravity",
  balance: "Perfect balance",
  slowmo: "Slow-mo",
  mirror: "Mirror mode",
  special: "Always special",
};
/** A line under each cheat in its list. */
export const CHEAT_DETAILS: Record<CheatId, string> = {
  moon: "Floaty airs: much less gravity off the ground.",
  balance: "Grinds, lips and manuals never tip. Also an accessibility option.",
  slowmo: "The whole game at half speed.",
  mirror: "The map flipped left to right. Reloads the map.",
  special: "The special meter stays full, so specials are always on.",
};

export function isCheatId(v: unknown): v is CheatId {
  return CHEATS.includes(v as CheatId);
}

/** Whether any cheat is on (the HUD's tag, and goals don't count). */
export function anyCheat(c: Cheats): boolean {
  return CHEATS.some((id) => c[id]);
}

export function toggleCheat(c: Cheats, id: CheatId): Cheats {
  return { ...c, [id]: !c[id] };
}

/** How fast game time runs against real time. */
export function gameSpeed(c: Cheats): number {
  return c.slowmo ? SLOWMO_SPEED : 1;
}

/** Saved cheats (untrusted): each one on only if it's saved as `true`. */
export function parseCheats(json: unknown): Cheats {
  const o = json && typeof json === "object" ? (json as Record<string, unknown>) : {};
  return Object.fromEntries(CHEATS.map((id) => [id, o[id] === true])) as Record<CheatId, boolean>;
}
